import {
    BillingInterval,
    ProviderCancellationStatus,
    ProviderEntitlementSnapshot,
    ProviderPaymentStatus,
} from '../interfaces';

const paymentStatuses = new Set<ProviderPaymentStatus>([
    'free',
    'active',
    'trialing',
    'past_due',
    'unpaid',
    'incomplete',
    'incomplete_expired',
    'canceled',
    'paused',
    'grace',
    'locked',
    'inactive',
    'unknown',
]);

export type ProviderBillingFields = Pick<ProviderEntitlementSnapshot,
    | 'planVersionId'
    | 'billingInterval'
    | 'paymentStatus'
    | 'validUntil'
    | 'currentPaidPeriodStartsAt'
    | 'currentPaidPeriodEndsAt'
    | 'billingRenewalAt'
    | 'cancelAtPeriodEnd'
    | 'cancellationStatus'
    | 'graceEndsAt'
    | 'recurringDisplayPrice'
    | 'recurringUnitDisplayPrice'
    | 'subscriptionUnits'
    | 'pricesIncludeTax'
    | 'currency'
>;

export type BillingDetailRow = {
    id: string;
    label: string;
    value: string;
};

const optionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim() ? value.trim() : undefined;

/** Parse only the sanitized billing fields exposed by getProviderLicenseUsage. */
export function parseProviderBillingFields(value: Record<string, unknown>): ProviderBillingFields {
    const interval = value.billingInterval;
    const billingInterval: BillingInterval | undefined = interval === 'monthly' || interval === 'annual' ?
        interval : undefined;
    const rawPaymentStatus = optionalString(value.paymentStatus);
    const paymentStatus = rawPaymentStatus && paymentStatuses.has(rawPaymentStatus as ProviderPaymentStatus) ?
        rawPaymentStatus as ProviderPaymentStatus : undefined;
    const rawCancellationStatus = optionalString(value.cancellationStatus);
    const cancellationStatus: ProviderCancellationStatus | undefined =
        rawCancellationStatus === 'none' || rawCancellationStatus === 'scheduled' ||
        rawCancellationStatus === 'completed' ? rawCancellationStatus : undefined;
    const parsedPrice = Number(value.recurringDisplayPrice);
    const parsedUnitPrice = Number(value.recurringUnitDisplayPrice);
    const parsedUnits = Number(value.subscriptionUnits);

    return {
        planVersionId: optionalString(value.planVersionId),
        billingInterval,
        paymentStatus,
        validUntil: optionalString(value.validUntil),
        currentPaidPeriodStartsAt: optionalString(value.currentPaidPeriodStartsAt),
        currentPaidPeriodEndsAt: optionalString(value.currentPaidPeriodEndsAt),
        billingRenewalAt: optionalString(value.billingRenewalAt),
        cancelAtPeriodEnd: value.cancelAtPeriodEnd === true,
        cancellationStatus,
        graceEndsAt: optionalString(value.graceEndsAt),
        recurringDisplayPrice: Number.isFinite(parsedPrice) && parsedPrice >= 0 ? parsedPrice : undefined,
        recurringUnitDisplayPrice: Number.isFinite(parsedUnitPrice) && parsedUnitPrice >= 0 ? parsedUnitPrice : undefined,
        subscriptionUnits: Number.isInteger(parsedUnits) && parsedUnits > 0 ? parsedUnits : undefined,
        pricesIncludeTax: false,
        currency: value.currency === 'GBP' ? 'GBP' : undefined,
    };
}

/** Format callable ISO dates and legacy Firestore timestamps consistently for billing UI. */
export function formatProviderBillingDate(value: unknown): string {
    if (value === undefined || value === null || value === '') return '';
    const rawDate = typeof (value as any)?.toDate === 'function' ? (value as any).toDate() : value;
    const date = rawDate instanceof Date ? rawDate : new Date(rawDate as any);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
}

const paymentStatusLabels: Record<ProviderPaymentStatus, string> = {
    free: 'Free',
    active: 'Active',
    trialing: 'Trial',
    past_due: 'Past due',
    unpaid: 'Unpaid',
    incomplete: 'Incomplete',
    incomplete_expired: 'Incomplete checkout expired',
    canceled: 'Canceled',
    paused: 'Paused',
    grace: 'Payment grace period',
    locked: 'Dashboard locked — contact SuperPenguin',
    inactive: 'Inactive',
    unknown: 'Unavailable',
};

/** Build concise label/value rows shared by the rendered billing summary and tests. */
export function buildProviderBillingDetails(
    entitlement: ProviderEntitlementSnapshot,
    familyAllowanceRenewsAt?: unknown
): BillingDetailRow[] {
    const rows: BillingDetailRow[] = [];
    if (entitlement.planVersionId) {
        rows.push({id: 'plan-version', label: 'Plan version', value: entitlement.planVersionId});
    }

    const intervalLabel = entitlement.billingInterval === 'monthly' ? 'Monthly' :
        entitlement.billingInterval === 'annual' ? 'Annual' : '';
    const price = entitlement.recurringDisplayPrice;
    if (price === 0) {
        rows.push({id: 'billing', label: 'Billing', value: 'Free'});
    } else if (intervalLabel) {
        const formattedPrice = typeof price === 'number' ? new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: entitlement.currency || 'GBP',
        }).format(price) : '';
        rows.push({
            id: 'billing',
            label: 'Billing',
            value: formattedPrice ? `${intervalLabel} · ${formattedPrice}/${entitlement.billingInterval === 'monthly' ? 'month' : 'year'} excluding VAT` : intervalLabel,
        });
    }

    if (entitlement.subscriptionUnits) {
        rows.push({
            id: 'subscription-units',
            label: 'Premium capacity',
            value: `${entitlement.subscriptionUnits} unit${entitlement.subscriptionUnits === 1 ? '' : 's'}`,
        });
    }

    const paymentStatus = entitlement.paymentStatus || entitlement.status;
    rows.push({
        id: 'payment-status',
        label: 'Payment status',
        value: paymentStatusLabels[paymentStatus] || 'Unavailable',
    });

    const periodStart = formatProviderBillingDate(entitlement.currentPaidPeriodStartsAt);
    const periodEnd = formatProviderBillingDate(entitlement.currentPaidPeriodEndsAt);
    if (periodStart || periodEnd) {
        rows.push({
            id: 'paid-period',
            label: 'Current paid period',
            value: periodStart && periodEnd ? `${periodStart} – ${periodEnd}` : `Ends ${periodEnd || periodStart}`,
        });
    }

    const billingRenewal = formatProviderBillingDate(entitlement.billingRenewalAt);
    if (billingRenewal) {
        rows.push({id: 'billing-renewal', label: 'Next billing date', value: billingRenewal});
    }

    const familyRenewal = formatProviderBillingDate(familyAllowanceRenewsAt);
    if (familyRenewal) {
        rows.push({id: 'family-renewal', label: 'Family allowance renewal', value: familyRenewal});
    }

    const graceEndsAt = formatProviderBillingDate(entitlement.graceEndsAt);
    if (graceEndsAt) {
        rows.push({id: 'payment-grace', label: 'Payment grace ends', value: graceEndsAt});
    }

    if (entitlement.billingInterval || entitlement.cancellationStatus === 'completed') {
        const cancellationValue = entitlement.cancellationStatus === 'scheduled' ?
            (periodEnd ? `Scheduled for ${periodEnd}` : 'Scheduled at the end of the current paid period') :
            entitlement.cancellationStatus === 'completed' ? 'Previous paid subscription ended' : 'Not scheduled';
        rows.push({id: 'cancellation', label: 'Cancellation', value: cancellationValue});
    }

    return rows;
}
